import React, { useEffect, useRef, useState } from 'react';
import { Typography, Button, notification, Form, Card, Tag } from 'antd';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getResellerData } from '@/store/reseller/selector';
import { getReseller, UpdateResellerBranding, updateTourStatus } from '@/api/reseller';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ResellerDashboard from '../ResellerDashboard';
import { ResellerSiderItem } from 'src/enums/IResellerSiderItems';
import { getTwilioBundleStatus } from '@/api/reservedNumbers';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { updateReseller } from '@/store/reseller/slice';
import FormEmail from '@/components/Form/Elements/FormEmail';
import FormPassword from '@/components/Form/Elements/FormPassword';
import FormDomain from '@/components/Form/Elements/FormDomain';
import FormSelect from '@/components/Form/Elements/FormSelect';
import { RocketOutlined } from '@mui/icons-material';
import TestEmailModal from './TestEmailModal';
import { BookOpenIcon, MailIcon } from 'lucide-react';
import TutorialsModal from '../TutorialsModal';
import { updateTutorialStepStatus } from '@/api/reseller';
import '../ResellerDashboard.scss';
import ResellerTour from '../ResellerTour/ResellerTour';
import TourButton from '@/components/TourButton/TourButton';
import { setTour } from '@/store/resellerTour/slice';
import { AppRoutes } from 'src/enums/ERoutes';
import { getResellerTourState } from '@/store/resellerTour/selector';
import { USER_TYPES } from 'src/enums/EUser';
import { MAILGUN_REGION_OPTIONS, DEFAULT_MAILGUN_REGION } from '../../../constants/mailgun';

const { Title, Text } = Typography;

const tutorialItems = [
	{
		icon: (
			<div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full">
				<MailIcon className="text-blue-500" size={20} />
			</div>
		),
		title: 'Configuring email for clients in My AI Frontdesk',
		description: 'Setting Up Mailgun with My AI Frontdesk for White Labeled Notification Sending',
		url: 'https://www.myaifrontdesk.com/reseller-tutorials/setting-up-mailgun-with-my-ai-frontdesk-for-white-labeled-notification-sending',
	},
];

const ResellerEmail: React.FC = () => {
	const [form] = Form.useForm();

	const resellerData = useAppSelector(getResellerData);
	const dispatch = useAppDispatch();
	const queryClient = useQueryClient();

	const [openTestModal, setOpenTestModal] = useState<boolean>(false);
	const [isTutorialsModalOpen, setIsTutorialsModalOpen] = useState(false);

	const emailConfigTourRef1 = useRef<HTMLDivElement>(null);
	const emailConfigTourRef2 = useRef<HTMLDivElement>(null);
	const emailConfigTourRef3 = useRef<HTMLDivElement>(null);

	const emailConfigTourSteps = [
		{
			title: (
				<h1 className="font-bold text-2xl">Send email notifications from your own email address</h1>
			),
			description: (
				<p className="text-base">
					If you've set up notifications for your clients in their dashboard, you can now send them
					using your own domain and email address. Simply connect your Mailgun account—free accounts
					include 3,000 emails!
				</p>
			),
			target: emailConfigTourRef1.current as HTMLElement,
		},
		{
			title: <h1 className="font-bold text-2xl">Default settings for notifications</h1>,
			description: (
				<p className="text-base">
					If you don't set up your own email with Mailgun, don't worry. By default, your clients
					will receive email notifications from our unbranded domain:
					notifications@mail.aireceptionist.org.
				</p>
			),
			target: emailConfigTourRef2.current as HTMLElement,
		},
		{
			title: <h1 className="font-bold text-2xl">Tutorial on how to connect Mailgun</h1>,
			description: (
				<p className="text-base">
					Not sure how to set up and/or connect your Mailgun account? No worries—we've got
					step-by-step tutorials to guide you. You'll find them right here.
				</p>
			),
			target: emailConfigTourRef3.current as HTMLElement,
		},
	];

	const { refetch } = useQuery({
		queryKey: ['reseller'],
		queryFn: () => getReseller({ from: USER_TYPES.RESELLER }),

		refetchOnWindowFocus: false,
		refetchOnMount: false,
	});

	const { data } = useQuery({
		queryKey: ['getTwilioBundleStatus', resellerData?.twilioBundleId],
		queryFn: () => getTwilioBundleStatus(resellerData?.twilioBundleId as string, true),
		enabled: !!resellerData?.twilioBundleId,
		staleTime: 5 * 60 * 1000, // Cache for 5 minutes
		retry: 3,
	});

	useEffect(() => {
		if (data) {
			dispatch(updateReseller({ twilioBundleStatus: data.status }));
		}
	}, [data]);

	const resellerTour = useAppSelector(getResellerTourState);

	useEffect(() => {
		if (!resellerData.emailAddress || resellerTour.open) {
			return;
		}

		if (!resellerData.tours?.email) {
			console.info('setting tour');

			updateToursMutation.mutate('email');
			dispatch(
				setTour({
					open: true,
					currentStep: 0,
					returnTo: AppRoutes.RESELLER_EMAIL_CONFIG,
				}),
			);
		}
	}, [resellerData.emailAddress, resellerTour.open]);

	const mutation = useMutation({
		mutationKey: ['updateResellerBranding'],
		mutationFn: UpdateResellerBranding,
		onError(data) {
			notification.error({
				message: data.message,
			});
		},
		onSuccess(data) {
			if (data.success) {
				queryClient.invalidateQueries({ queryKey: ['reseller'] });
				notification.success({
					key: 'update-reseller',
					message: 'Updated successfully',
					duration: 3,
				});
				refetch();
			}
		},
	});

	const updateTutorialMutation = useMutation({
		mutationFn: updateTutorialStepStatus,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['reseller'] });
		},
	});

	const updateToursMutation = useMutation({
		mutationFn: updateTourStatus,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['reseller'] });
		},
	});

	const handleTutorialClick = () => {
		if (!resellerData.tutorial_steps?.email_tutorial) {
			updateTutorialMutation.mutate('email_tutorial');
		}
		setIsTutorialsModalOpen(true);
	};

	const handleTourClick = () => {
		setIsTutorialsModalOpen(false);

		if (!resellerData.tours?.email) {
			updateToursMutation.mutate('email');
		}
	};

	const getDomainFromEmail = (email?: string) => {
		if (email && email.includes('@')) {
			return email.split('@')[1];
		}
		return '';
	};

	const handleSubmit = async (values: {
		resellerMailgunDomain?: string;
		resellerMailgunEmail?: string;
		resellerMailgunRegion?: string;
		resellerMailgunUsername?: string;
		resellerMailgunAPIKey?: string;
	}) => {
		try {
			await form.validateFields();

			const domain = getDomainFromEmail(values.resellerMailgunEmail);
			if (domain !== values.resellerMailgunDomain) {
				notification.error({
					message: (
						<span>
							Email <strong>{values.resellerMailgunEmail}</strong> should be from same domain{' '}
							<strong>{values.resellerMailgunDomain}</strong>
						</span>
					),
				});
				return;
			}

			await mutation.mutate({
				emailAddress: resellerData.emailAddress as string,
				resellerMailgunDomain: values.resellerMailgunDomain,
				resellerMailgunEmail: values.resellerMailgunEmail,
				resellerMailgunRegion: values.resellerMailgunRegion,
				resellerMailgunUsername: values.resellerMailgunUsername,
				resellerMailgunAPIKey: values.resellerMailgunAPIKey,
			});
		} catch {
			notification.error({ message: 'All fields required' });
		}
	};

	return (
		<ResellerDashboard activeKey={ResellerSiderItem.EMAIL_CONFIG}>
			<div className="p-8">
				<div className="flex items-center justify-between mb-6">
					<div>
						<Title level={2} className="!mb-2">
							Configure Email Notifications
						</Title>
						<Text type="secondary" className="block max-w-2xl">
							Send call notifications to your clients via your own email using Mailgun integration.
							Simply connect your Mailgun account to send notification from your own domain.
						</Text>
					</div>

					<div className="flex items-center gap-2">
						<Button
							type="default"
							size="large"
							className={
								!resellerData?.tutorial_steps?.plan_creation_tutorial
									? 'bg-[#F1F8FF] hover:bg-[#F1F8FF] text-blue-600 has-ping overflow-hidden'
									: ''
							}
							onClick={handleTutorialClick}
						>
							<BookOpenIcon size={16} />
							<span>Tutorials</span>
						</Button>
					</div>
				</div>

				{!resellerData.branded && (
					<>
						<Card className="border shadow mb-6">
							<div className="flex items-center justify-between">
								<div>
									<Title level={4} className="!mb-0">
										Current Configuration
									</Title>
									<Text type="secondary">
										Email notifications are currently being sent from our grey labeled email
										address.
									</Text>
								</div>
								<Tag color="blue" className="rounded-xl px-2">
									Default
								</Tag>
							</div>
							<div className="bg-gray-100 p-3 rounded mt-4">
								<p className="text-gray-500 my-0">
									Currently using default email notifications@mail.aireceptionist.org
								</p>
								<p className="text-gray-500 my-0">
									All client notifications will be sent from this address unless you configure your
									own email below.
								</p>
							</div>
						</Card>
					</>
				)}

				<Card className="border !border-blue-500 shadow">
					<div className="flex justify-between items-center">
						<div>
							<Title level={4}>Use Your Own Email (Fully White Labeled)</Title>
							<Text>Connect Mailgun to send email notifications from your own domain.</Text>
						</div>
						<Tag className="rounded-xl px-2" color="#108ee9">
							Recommended
						</Tag>
					</div>

					<div ref={emailConfigTourRef1}>
						<Form
							form={form}
							onFinish={handleSubmit}
							layout="vertical"
							className="mt-4"
							initialValues={{
								resellerMailgunDomain: resellerData?.mailgunCreds?.domain,
								resellerMailgunEmail: resellerData?.mailgunCreds?.email,
								resellerMailgunUsername: resellerData?.mailgunCreds?.username,
								resellerMailgunAPIKey: resellerData?.mailgunCreds?.apiKey,
								resellerMailgunRegion: resellerData?.mailgunCreds?.region || DEFAULT_MAILGUN_REGION,
							}}
						>
							<FormDomain name="resellerMailgunDomain" label="Email Domain" />
							<FormEmail name="resellerMailgunEmail" label="From Address" rules={[]} />
							<FormSelect
								name="resellerMailgunRegion"
								label="Domain Region"
								placeholder="Select region"
								options={MAILGUN_REGION_OPTIONS}
							/>
							<FormPassword
								name="resellerMailgunUsername"
								label="Mailgun Username"
								placeholder="Enter Mailgun Username"
								rules={[]}
							/>
							<FormPassword
								name="resellerMailgunAPIKey"
								label="Mailgun API Key"
								placeholder="Enter Mailgun API Key"
								rules={[]}
							/>
							<div className="mt-8 text-right">
								<Button
									type="primary"
									size="large"
									className="text-white"
									loading={mutation.isPending}
									onClick={() => handleSubmit(form.getFieldsValue())}
								>
									Save Changes
								</Button>
								{resellerData?.mailgunCreds?.domain &&
									resellerData?.mailgunCreds?.address &&
									resellerData?.mailgunCreds?.region &&
									resellerData?.mailgunCreds?.username &&
									resellerData?.mailgunCreds?.apiKey && (
										<Button
											type="default"
											size="large"
											className="ms-3"
											icon={<RocketOutlined />}
											onClick={() => setOpenTestModal(true)}
										>
											Test Email
										</Button>
									)}
								<TestEmailModal
									open={openTestModal}
									setOpen={setOpenTestModal}
									resellerData={resellerData}
								/>
							</div>
						</Form>
					</div>
				</Card>

				<div className="my-4">
					<Text className="my-0">
						Need help setting up Mailgun? Check our{' '}
						<a
							href="https://www.mailgun.com/resources/videos/getting-started-with-mailgun/"
							className="text-blue-500"
							target="_blank"
							rel="noreferrer"
						>
							setup guide
						</a>
					</Text>
				</div>
				<TutorialsModal
					isOpen={isTutorialsModalOpen}
					onClose={() => setIsTutorialsModalOpen(false)}
					tutorialItems={tutorialItems}
				>
					<TourButton onClick={handleTourClick} page="email" />
				</TutorialsModal>
			</div>
			<ResellerTour tourSteps={emailConfigTourSteps} />
		</ResellerDashboard>
	);
};

export default ResellerEmail;
